/**
 * GoalsCategoryPanel (Story 37.5 → Story 18.4 Saved/Custom modes)
 *
 * The `goals` sidebar category's floating-panel body. Two modes via a §10.2
 * segmented control at the top:
 *
 *  - **Saved** (Story 18.4): lists the client's persisted budgets ("Budget
 *    Progress") and goals ("Goal Progress"). Clicking a row's Add binds a NEW
 *    `goal` widget to that record — the binding key (`savedBudgetId` /
 *    `savedGoalId`) is the ONLY data key written; everything else hydrates
 *    server-side at populate time. A one-click "Add budget & goal dashboard"
 *    button (when ≥1 budget AND ≥1 goal exist) drops a heading + capped bound
 *    widgets in ONE atomic add via `onPresetWidgetsAdd`.
 *  - **Custom** (Story 37.5): the existing configure-then-add draft builder —
 *    `GoalConfigSection` + `isGoalDraftValid` + Add, BYTE-UNCHANGED.
 *
 * Default segment: Saved when the client has ≥1 saved goal/budget, else Custom.
 * Mock mode (clientId undefined — the editor's unconnected state): the Saved
 * segment is DISABLED with a hint; Custom stays fully functional.
 *
 * Reuse, don't fork: the Custom controls are the SHARED `GoalConfigSection`
 * from `widgetDataForms` (the same sub-form `WidgetConfigPanel` uses — 37.5
 * anti-drift). Icons are Phosphor only (§5/§10.6 — the legacy lucide `Flag`
 * import was swapped per §10.6 "when you touch a file").
 */

import { useState, useMemo, useCallback, useEffect } from "react";
import { Flag, Plus } from "@phosphor-icons/react";
import type { WidgetConfig } from "../../lib/templateUtils";
import { DEFAULT_WIDGET_CONFIGS, DEFAULT_WIDGET_TITLES } from "./canvasWidgetDefaults";
import { GoalConfigSection, isGoalDraftValid } from "./widgetDataForms";
import type { CategoryPanelProps } from "./categoryPickerRegistry";
import { useClientGoals } from "../../hooks/useClientGoals";
import { useClientBudgets } from "../../hooks/useClientBudgets";
import { formatGoalValue } from "../../lib/goalFormat";
import { expandBudgetGoalDashboardPreset } from "../../lib/widgetPresets";

/** Synthetic draft-widget id — stable, never persisted (Pre-mortem A). */
const DRAFT_GOAL_WIDGET_ID = "__draft_goal__";

type GoalPanelMode = "saved" | "custom";

/** Title-case a platform slug ("google-ads" → "Google Ads") or "All platforms". */
function platformLabel(integrationType: string): string {
  if (integrationType === "all") return "All platforms";
  return integrationType
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Short recurring-period badge text. */
function periodBadge(periodType: string): string {
  return periodType.charAt(0).toUpperCase() + periodType.slice(1);
}

export function GoalsCategoryPanel({
  onWidgetAdd,
  onPresetWidgetsAdd,
  clientId,
}: CategoryPanelProps) {
  // useClientGoals (18.3) returns the raw TanStack query result (data:
  // GoalListItem[]); useClientBudgets (18.4) returns { budgets }.
  const { data: goals = [] } = useClientGoals(clientId);
  const { budgets } = useClientBudgets(clientId);
  const hasSaved = goals.length > 0 || budgets.length > 0;
  // Mock mode (no client) can never use Saved — force Custom + disable Saved.
  const savedDisabled = !clientId;

  // Default to Saved when the client has ≥1 saved record, else Custom. We track
  // whether the user has manually switched so we don't override their choice
  // when the async query resolves (the default is a one-time initial pick).
  const [mode, setMode] = useState<GoalPanelMode>("custom");
  const [userPicked, setUserPicked] = useState(false);
  useEffect(() => {
    if (userPicked) return;
    setMode(!savedDisabled && hasSaved ? "saved" : "custom");
  }, [savedDisabled, hasSaved, userPicked]);

  const pickMode = useCallback((next: GoalPanelMode) => {
    setUserPicked(true);
    setMode(next);
  }, []);

  // ── Custom draft builder (37.5 — unchanged) ─────────────────────────
  const [draft, setDraft] = useState<Record<string, unknown>>(() =>
    structuredClone(DEFAULT_WIDGET_CONFIGS.goal),
  );
  const draftWidget = useMemo<WidgetConfig>(
    () => ({
      id: DRAFT_GOAL_WIDGET_ID,
      type: "goal" as const,
      title: DEFAULT_WIDGET_TITLES.goal,
      position: { row: 0, col: 0, colSpan: 6, rowSpan: 3 },
      config: draft,
    }),
    [draft],
  );
  const handleDraftUpdate = useCallback(
    (_id: string, changes: Partial<WidgetConfig>) => {
      if (!changes.config) return;
      setDraft((d) => ({ ...d, ...(changes.config as Record<string, unknown>) }));
    },
    [],
  );
  const canAddCustom = isGoalDraftValid(draft) && typeof onWidgetAdd === "function";
  const handleAddCustom = useCallback(() => {
    if (!canAddCustom || !onWidgetAdd) return;
    const added = onWidgetAdd("goal", structuredClone(draft));
    if (added) setDraft(structuredClone(DEFAULT_WIDGET_CONFIGS.goal));
  }, [canAddCustom, onWidgetAdd, draft]);

  // ── Saved-mode row adds (Story 18.4) ────────────────────────────────
  const addBudgetWidget = useCallback(
    (budgetId: string, title: string) => {
      // Binding key is the ONLY data key; title seeds the canvas placeholder.
      // goalType seeds the placeholder + keeps the schema's bound branch happy.
      onWidgetAdd?.("goal", { savedBudgetId: budgetId, goalType: "budget", title });
    },
    [onWidgetAdd],
  );
  const addGoalWidget = useCallback(
    (goalId: string, title: string) => {
      onWidgetAdd?.("goal", { savedGoalId: goalId, title });
    },
    [onWidgetAdd],
  );

  const canAddDashboard =
    budgets.length > 0 &&
    goals.length > 0 &&
    typeof onPresetWidgetsAdd === "function";
  const handleAddDashboard = useCallback(() => {
    if (!canAddDashboard || !onPresetWidgetsAdd) return;
    onPresetWidgetsAdd(
      expandBudgetGoalDashboardPreset(
        budgets.map((b) => ({ id: b.id, integrationType: b.integrationType })),
        goals.map((g) => ({ id: g.id, name: g.name })),
      ),
    );
  }, [canAddDashboard, onPresetWidgetsAdd, budgets, goals]);

  return (
    <div className="flex h-full flex-col p-3" data-testid="goals-category-panel">
      <div className="mb-3 flex items-center gap-2">
        <Flag size={18} className="text-muted-foreground" />
        <h3 className="text-[13px] font-semibold text-foreground">Goals</h3>
      </div>

      {/* Segmented control (§10.2: h-9 rounded-md). Saved disabled in mock mode. */}
      <div
        role="tablist"
        aria-label="Goal source"
        data-testid="goals-mode-segmented"
        className="mb-3 flex h-9 items-center gap-1 rounded-md border-[0.5px] border-input bg-muted/40 p-0.5"
      >
        <button
          type="button"
          role="tab"
          aria-selected={mode === "saved"}
          disabled={savedDisabled}
          data-testid="goals-mode-saved"
          title={savedDisabled ? "Select a client to use saved goals" : undefined}
          onClick={() => pickMode("saved")}
          className={`flex-1 cursor-pointer rounded-[5px] px-2 text-[12.5px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
            mode === "saved"
              ? "bg-background text-foreground shadow-xs"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          Saved
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === "custom"}
          data-testid="goals-mode-custom"
          onClick={() => pickMode("custom")}
          className={`flex-1 cursor-pointer rounded-[5px] px-2 text-[12.5px] font-medium transition-colors ${
            mode === "custom"
              ? "bg-background text-foreground shadow-xs"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          Custom
        </button>
      </div>

      {mode === "saved" && !savedDisabled ? (
        <div className="flex-1 overflow-y-auto" data-testid="goals-saved-list">
          {!hasSaved ? (
            <div className="space-y-1 px-1 py-4" data-testid="goals-saved-empty">
              <p className="text-[11px] text-muted-foreground">
                No goals or budgets configured for this client yet
              </p>
              <p className="text-[11px] text-muted-foreground/80">
                Add them in client settings, then bind them here.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Budget Progress group */}
              {budgets.length > 0 && (
                <div className="space-y-1" data-testid="goals-budget-group">
                  <p className="text-[11px] font-medium text-muted-foreground">
                    Budget Progress
                  </p>
                  {budgets.map((b) => {
                    const label = platformLabel(b.integrationType);
                    const amount = formatGoalValue(b.amount, "currency", b.currencyCode);
                    const rowTitle = `${label} budget`;
                    return (
                      <button
                        key={b.id}
                        type="button"
                        data-testid={`goals-budget-row-${b.id}`}
                        onClick={() => addBudgetWidget(b.id, rowTitle)}
                        title={`${rowTitle} — ${amount} (${periodBadge(b.periodType)})`}
                        className="flex h-9 w-full cursor-pointer items-center gap-2 rounded-lg border-[0.5px] border-input bg-background px-2 text-left text-[12.5px] shadow-xs transition-colors hover:bg-accent/50"
                      >
                        <span className="min-w-0 flex-1 truncate text-foreground">
                          {label}
                          <span className="text-muted-foreground"> · {amount}</span>
                        </span>
                        <span className="shrink-0 rounded bg-muted px-1 text-[10px] text-muted-foreground">
                          {periodBadge(b.periodType)}
                        </span>
                        <Plus size={14} className="shrink-0 text-primary" />
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Goal Progress group */}
              {goals.length > 0 && (
                <div className="space-y-1" data-testid="goals-goal-group">
                  <p className="text-[11px] font-medium text-muted-foreground">
                    Goal Progress
                  </p>
                  {goals.map((g) => {
                    const target = formatGoalValue(g.targetValue, g.unit, g.currencyCode);
                    return (
                      <button
                        key={g.id}
                        type="button"
                        data-testid={`goals-goal-row-${g.id}`}
                        onClick={() => addGoalWidget(g.id, g.name)}
                        title={`${g.name} — target ${target} (${periodBadge(g.periodType)})`}
                        className="flex h-9 w-full cursor-pointer items-center gap-2 rounded-lg border-[0.5px] border-input bg-background px-2 text-left text-[12.5px] shadow-xs transition-colors hover:bg-accent/50"
                      >
                        <span className="min-w-0 flex-1 truncate text-foreground">
                          {g.name}
                          <span className="text-muted-foreground"> · {target}</span>
                        </span>
                        <span className="shrink-0 rounded bg-muted px-1 text-[10px] text-muted-foreground">
                          {periodBadge(g.periodType)}
                        </span>
                        <Plus size={14} className="shrink-0 text-primary" />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto">
          <GoalConfigSection widget={draftWidget} onUpdate={handleDraftUpdate} />
        </div>
      )}

      {/* Bottom actions (§10.5 skeleton) */}
      {mode === "saved" && !savedDisabled ? (
        canAddDashboard ? (
          <button
            type="button"
            data-testid="goals-dashboard-preset-btn"
            onClick={handleAddDashboard}
            className="mt-3 w-full cursor-pointer rounded-md border border-input bg-primary px-3 py-2 text-[13px] font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Add budget &amp; goal dashboard
          </button>
        ) : null
      ) : (
        <button
          type="button"
          data-testid="goals-panel-add-btn"
          disabled={!canAddCustom}
          onClick={handleAddCustom}
          className="mt-3 w-full rounded-md border border-input bg-primary px-3 py-2 text-[13px] font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Add to report
        </button>
      )}
    </div>
  );
}
